<template>
  <div v-if="filters && filters.length > 0" class="flex-1">
    <div class="flex flex-wrap items-start gap-x-2 gap-y-2 min-h-fit">
      <div
        v-for="(filter, index) in visibleFilters.filter(
          (item) => item.type || item.render
        )"
        :key="index"
        class="filter-item flex-shrink-0 min-h-[32px]"
        :data-testid="filter.dataTestId || `filter-${filter.name}`"
      >
        <component
          :is="componentMapping(filter.type)"
          v-if="(filter.type || filter.render) && componentMapping(filter.type)"
          :model-value="getFilterModelValue(filter)"
          v-bind="filter"
          :placeholder="filter.label"
          :i18n-config="i18nConfig"
          :filter="filter"
          :use-old="filter.useOld"
          :section-title="filtersState[filter.name]?.title || filter.label"
          :locale="locale"
          :tags="filtersState"
          :append-to-body="filter.appendToBody"
          :filter-categories="filterCategories"
          :always-visible-filters="alwaysVisibleFilters || []"
          @update:modelValue="(value) => updateFilterValue(filter.name, value)"
          @invalid-filters="handleInvalidFilters"
          @resource-set="(data) => handleInitialFetch(filter, data)"
          @keyup.enter="handleEnter"
          @submit="handleSubmit"
        />
      </div>

      <div
        v-if="showMoreFiltersButton"
        class="filter-item flex-shrink-0 min-h-[32px]"
      >
        <popup-pills-filters-visibility
          :label="$t('pages.orders_transactions.all.filters.more.label')"
          :model-value="selectedVisibleFilters"
          :filter="{
            options: availableFilterOptions,
            searchPlaceholder: $t(
              'pages.orders_transactions.all.filters.search'
            ),
            noFiltersFoundLabel: $t(
              'pages.orders_transactions.all.filters.more.no_filters_found'
            )
          }"
          :filter-categories="filterCategories"
          :always-visible-filters="alwaysVisibleFilters"
          @update:model-value="updateSelectedVisibleFilters"
          @submit="handleMoreFiltersSubmit"
        />
      </div>

      <div
        v-if="hasActiveFilters"
        class="filter-item flex-shrink-0 reset-button min-h-[32px]"
      >
        <div class="vertical-divider" />
        <button type="button" class="reset-text" @click="resetAllFilters">
          {{ $t('pages.orders_transactions.all.filters.reset') }}
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import mapValues from 'just-map-values'
import compare from 'just-compare'
import { defineAsyncComponent } from 'vue'

export default {
  name: 'FiltersGroup',
  components: {
    PopupMultiselect: defineAsyncComponent(
      () => import('./filter/popup-filters/popup-multiselect.vue')
    ),
    PopupInput: defineAsyncComponent(
      () => import('./filter/popup-filters/popup-input.vue')
    ),
    PopupNumberRangeInput: defineAsyncComponent(
      () => import('./filter/popup-filters/popup-number-range-input.vue')
    ),
    PopupSimpleNumberInput: defineAsyncComponent(
      () => import('./filter/popup-filters/popup-simple-number-input.vue')
    ),
    PopupCardInput: defineAsyncComponent(
      () => import('./filter/popup-filters/popup-card-input.vue')
    ),
    PopupSelect: defineAsyncComponent(
      () => import('./filter/popup-filters/popup-select.vue')
    ),
    SearchFilterDaterange: defineAsyncComponent(
      () => import('./filter/daterange-wrapper.vue')
    ),
    SearchFilterDate: defineAsyncComponent(
      () => import('./filter/date-wrapper.vue')
    ),
    PopupIpInput: defineAsyncComponent(
      () => import('./filter/popup-filters/popup-ip-input.vue')
    ),
    PopupExpiryDateInput: defineAsyncComponent(
      () => import('./filter/popup-filters/popup-expiry-date-input.vue')
    ),
    PopupSaleStream: defineAsyncComponent(
      () => import('./filter/popup-filters/popup-sale-stream.vue')
    ),
    PopupPillsFiltersVisibility: defineAsyncComponent(
      () => import('./filter/popup-filters/popup-pills-filters-visibility.vue')
    )
  },
  props: {
    locale: {
      type: String,
      default: 'en'
    },
    modelValue: {
      type: Object,
      required: false,
      default: () => ({})
    },
    filters: {
      type: Array,
      required: true
    },
    textFieldName: {
      type: String,
      required: false,
      default: 'q'
    },
    submitOnEnter: {
      type: Boolean,
      required: false,
      default: false
    },
    submitOnItem: {
      type: Boolean,
      required: false,
      default: false
    },
    dynamicFilters: {
      type: Boolean,
      default: false
    },
    filterCategories: {
      type: Object,
      default: () => ({})
    },
    alwaysVisibleFilters: {
      type: Array,
      default: () => []
    },
    showMoreFiltersButton: {
      type: Boolean,
      default: true
    }
  },
  emits: [
    'filter-input',
    'filter',
    'resource-set',
    'invalid-filters',
    'submit',
    'reset',
    'update-filter'
  ],
  data() {
    return {
      invalidFilters: false,
      filtersState: this.normaliseIncomingFilters(),
      selectedVisibleFilters: this.loadVisibleFiltersFromStorage()
    }
  },
  computed: {
    i18nConfig() {
      return {
        inputPlaceholder: this.$t(
          'components.th_datatable.search_filter.search.placeholder'
        ),
        selectPlaceholder: this.$t(
          'components.th_datatable.search_filter.select.placeholder'
        ),
        searchButtonText: this.$t(
          'components.th_datatable.search_filter.search.button'
        ),
        resetButtonText: this.$t(
          'components.th_datatable.search_filter.search.reset'
        )
      }
    },
    searchDisabled() {
      return !Object.keys(this.filtersState).length
    },
    hasActiveFilters() {
      // Check if there are active filter values
      const hasFilterValues = Object.values(this.filtersState).some(
        (filter) => filter.value !== null && filter.value !== ''
      )

      // Check if there are additional visible filters (more filters applied)
      const hasVisibleFilters =
        this.selectedVisibleFilters && this.selectedVisibleFilters.length > 0

      return hasFilterValues || hasVisibleFilters
    },
    visibleFilters() {
      if (!this.filters) return []

      // Always visible filters from props
      const alwaysVisible = this.alwaysVisibleFilters || []

      // Get selected visible filters from the more filters control
      const selectedFilters = this.selectedVisibleFilters || []

      return this.filters.filter((filter) => {
        return (
          alwaysVisible.includes(filter.name) ||
          selectedFilters.includes(filter.name)
        )
      })
    },
    // Get all filters except the always visible ones for the visibility control
    availableFilterOptions() {
      if (!this.filters) return []

      const alwaysVisible = this.alwaysVisibleFilters || []

      return this.filters
        .filter((filter) => !alwaysVisible.includes(filter.name))
        .map((filter) => ({
          name: filter.name,
          label: filter.label
        }))
    }
  },
  watch: {
    filters: {
      handler: function (newValues, oldValues) {
        if (this.dynamicFilters && !compare(newValues, oldValues)) {
          this.filtersState = this.normaliseIncomingFilters()
        }
      }
    },
    filtersState: {
      handler: function (newValues, oldValues) {
        // Ensure moreFilters is never in filtersState
        if (newValues.moreFilters) {
          this.$nextTick(() => {
            delete this.filtersState.moreFilters
          })
        }

        if (!compare(newValues, oldValues)) {
          this.$emit('update:modelValue', newValues)
        }
      },
      deep: true
    },
    modelValue(newValues, oldValues) {
      if (!compare(newValues, oldValues)) {
        this.filtersState = this.normaliseIncomingFilters()
      }
    },
    // Watch for changes in visible filters and clear values of hidden filters
    visibleFilters(newVisibleFilters, oldVisibleFilters) {
      // Skip during initialization or if no previous filters
      if (this.isInitializing || !oldVisibleFilters) return

      const newVisibleNames = newVisibleFilters.map((f) => f.name)
      const oldVisibleNames = oldVisibleFilters.map((f) => f.name)

      // Find filters that were removed from visibility
      const removedFilters = oldVisibleNames.filter(
        (name) => !newVisibleNames.includes(name)
      )

      // Clear values of removed filters
      removedFilters.forEach((filterName) => {
        if (
          this.filtersState[filterName] &&
          this.filtersState[filterName].value !== null
        ) {
          this.filtersState[filterName].value = null
        }
      })

      // If any filters were cleared, dispatch and trigger data reload
      if (removedFilters.length > 0) {
        this.dispatchFilter()
        this.$emit('submit', this.handleSubmit())
      }
    }
  },
  methods: {
    getFilterModelValue(filter) {
      const value = this.filtersState[filter.name]?.value

      // A URL saved while a filter was a multiselect can still contain an
      // array after that filter becomes a single select. Keep PopupSelect's
      // model scalar so its radio option and displayed value stay in sync.
      if (filter.type === 'select' && Array.isArray(value)) {
        return value[0] ?? null
      }

      return value
    },
    componentMapping(type) {
      const mapping = {
        multiselect: 'PopupMultiselect',
        input: 'PopupInput',
        'number-input': 'PopupSimpleNumberInput',
        'currency-input': 'PopupNumberRangeInput',
        'card-input': 'PopupCardInput',
        select: 'PopupSelect',
        daterange: 'SearchFilterDaterange',
        date: 'SearchFilterDate',
        timestamp: 'SearchFilterDaterange',
        'ip-input': 'PopupIpInput',
        'expiry-date-input': 'PopupExpiryDateInput',
        salesstream: 'PopupSaleStream'
      }
      return mapping[type] || null
    },
    normaliseIncomingFilters() {
      // Exclude moreFilters from modelValue as it's UI-only state
      const modelValueWithoutMoreFilters = Object.keys(
        this.modelValue || {}
      ).reduce((acc, key) => {
        if (key !== 'moreFilters') {
          acc[key] = this.modelValue[key]
        }
        return acc
      }, {})

      return this.filters.reduce(
        (incomingFilters, item) => {
          incomingFilters[item.name] = {
            value: null,
            ...item,
            ...incomingFilters[item.name]
          }
          return incomingFilters
        },
        {
          [this.textFieldName]: { label: null, value: null },
          ...modelValueWithoutMoreFilters
        }
      )
    },
    dispatchFilter() {
      const filter = {}
      Object.entries(this.filtersState)
        .filter((item) => {
          // Skip moreFilters as it's UI-only state
          if (item[0] === 'moreFilters') return false

          const isEmpty =
            item.value === null ||
            item.value === '' ||
            item[1].value === null ||
            item[1].value === '' ||
            this.isEmptyArray(item[1].value)

          return !isEmpty
        })
        .forEach((item) => {
          filter[item[0]] = item[1].value
        })

      this.$emit('filter', filter, this.filtersState)
      if (this.submitOnItem) {
        this.handleSubmit()
      }
    },
    handleSubmit() {
      try {
        if (this.invalidFilters) return

        // Exclude moreFilters from submission as it's UI-only state
        const filtersStateWithoutMoreFilters = Object.keys(
          this.filtersState
        ).reduce((acc, key) => {
          if (key !== 'moreFilters') {
            acc[key] = this.filtersState[key]
          }
          return acc
        }, {})

        const trimmedFiltersState = this.prepareFiltersStateObject(
          filtersStateWithoutMoreFilters
        )
        this.$emit('submit', trimmedFiltersState)
        return trimmedFiltersState
      } catch {}
    },
    handleInvalidFilters(invalid) {
      this.invalidFilters = invalid
    },
    prepareFiltersStateObject(filtersState) {
      function trimInputs(obj) {
        if (!obj || typeof obj !== 'object') return obj

        for (const key in obj) {
          if (obj[key] !== null && typeof obj[key] === 'object') {
            // works on objects and arrays
            obj[key] = trimInputs(obj[key])
          }
          if (typeof obj[key] === 'string') {
            obj[key] = obj[key].trim()
          }
        }
        return obj
      }

      try {
        const trimmed = { ...filtersState }
        // remove trailing whitespace in all nested levels
        trimInputs(trimmed)
        return trimmed
      } catch {
        return filtersState // Return original object if trimming fails
      }
    },
    handleSearchClick() {
      this.handleSubmit()
    },
    reset() {
      this.filtersState = this.normaliseIncomingFilters()
      this.filtersState = mapValues(this.filtersState, (value) => {
        value.value = value.default ?? null
        return value
      })
      this.$emit('reset')
      this.$emit('submit', this.handleSubmit())
    },
    handleEnter() {
      if (this.submitOnEnter) {
        this.handleSubmit()
      }
    },
    isEmptyArray(value) {
      return Array.isArray(value) && !value.length
    },
    handleInitialFetch(filter, data) {
      this.filtersState[filter.name].originalData = data
      this.$emit('update-filter', this.filtersState[filter.name])
    },
    updateFilterValue(name, value) {
      // Ensure filtersState and the specific filter exist
      if (!this.filtersState || !this.filtersState[name]) {
        return
      }

      this.filtersState[name].value = value

      this.dispatchFilter()
    },
    resetAllFilters() {
      this.filtersState = mapValues(this.filtersState, (value, key) => {
        // Don't reset moreFilters as it's UI-only state
        if (key === 'moreFilters') return value
        value.value = value.default ?? null
        return value
      })

      this.dispatchFilter()
      this.$emit('reset')
      // Emit submit event to trigger data reload with cleared filters
      this.$emit('submit', this.handleSubmit())
    },
    updateSelectedVisibleFilters(selectedFilters) {
      this.selectedVisibleFilters = selectedFilters
      this.saveVisibleFiltersToStorage(selectedFilters)
    },
    handleMoreFiltersSubmit() {
      // This method intentionally does nothing as the filter visibility
      // should not trigger any backend requests or route changes
      // The visibility change is purely a frontend UI state change
    },
    loadVisibleFiltersFromStorage() {
      try {
        const storageKey = `moreFilters_${this.$route?.path || 'default'}`
        const stored = localStorage.getItem(storageKey)
        return stored ? JSON.parse(stored) : []
      } catch {
        return []
      }
    },
    saveVisibleFiltersToStorage(selectedFilters) {
      try {
        const storageKey = `moreFilters_${this.$route?.path || 'default'}`
        localStorage.setItem(storageKey, JSON.stringify(selectedFilters))
      } catch {
        // Silently fail if localStorage is not available
      }
    }
  }
}
</script>

<style scoped>
.filter-item {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

:deep(.filter-header) {
  height: auto !important;
  min-height: auto !important;
  max-height: none !important;
}

.reset-button {
  display: flex;
  align-items: center;
}

.vertical-divider {
  width: 1px;
  height: 20px;
  background-color: #e5e7eb;
  margin-right: 12px;
}

.reset-text {
  background: none;
  border: none;
  color: #3b82f6;
  cursor: pointer;
  font-size: 14px;
  text-decoration: none;
  padding: 0;
  margin: 0;
  height: auto;
  line-height: normal;
}

.reset-text:hover {
  color: #1d4ed8;
  text-decoration: none;
}

.reset-text:focus {
  outline: none;
  color: #1d4ed8;
}
</style>
